"use client";

import { useRef } from "react";
import {
  Eyebrow,
  SectionShell,
  useBandRowAlign,
  useWholeRowBlocks,
} from "@/components/sections/kit";
import {
  useEnterStagger,
  useMaskedReveal,
} from "@/components/sections/kit/motion";
import { routePath } from "@/lib/routes";
import { INFRA_CHECKS, MONITORED } from "./data";
import PinkCheck from "./PinkCheck";

/**
 * Infrastructure & availability — the list of what the public status page
 * monitors (no figures here: live p50/p99 and uptime live on /status) beside
 * the availability claims. Card left, claim right, one shared clearing block.
 */
export default function InfrastructureSection() {
  const rootRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  useWholeRowBlocks(rootRef);
  useBandRowAlign(rootRef);
  useMaskedReveal(copyRef, { heading: "h2" });
  useEnterStagger(rootRef, "[data-latency-card]", { y: 26 });
  useEnterStagger(copyRef, "[data-check-row]", { y: 14, delay: 0.15 });


  return (
    <SectionShell className="md:pt-px md:pb-px">
      <div ref={rootRef}>
        {/* One block — card and claim share a single clearing. */}
        <div
          data-grid-block
          className="grid items-center gap-10 lg:grid-cols-[480px_minmax(0,1fr)] lg:gap-16"
        >
          {/* The monitor card — names only; the numbers live on /status. */}
          <div
            ref={cardRef}
            data-latency-card
            className="rounded-ref-lg border border-line bg-surface px-[26px] py-6 shadow-ref-lg max-lg:order-2"
          >
            <div className="flex items-baseline gap-2.5 border-b border-line pb-3.5">
              <span className="font-jetbrains text-[11px] font-semibold uppercase tracking-[0.12em]">
                monitored on /status
              </span>
              <a
                href={routePath("status")}
                className="ml-auto font-jetbrains text-[10.5px] text-dim hover:text-brand-600"
              >
                live numbers ↗
              </a>
            </div>
            <div className="flex flex-col gap-[13px] pt-4">
              {MONITORED.map((row) => (
                <div
                  key={row.name}
                  className="grid grid-cols-[84px_minmax(0,1fr)_auto] items-center gap-3.5 sm:grid-cols-[100px_minmax(0,1fr)_118px]"
                >
                  <span
                    className={`whitespace-nowrap text-[13px] ${row.primary ? "font-bold" : "font-medium"}`}
                  >
                    {row.name}
                  </span>
                  <span aria-hidden="true" className="block border-b border-dashed border-line-2" />
                  <span
                    className={`justify-self-end whitespace-nowrap font-jetbrains text-xs ${
                      row.primary ? "font-bold text-brand-600" : "text-muted"
                    }`}
                  >
                    {row.note}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* The claim + availability facts. */}
          <div ref={copyRef} className="min-w-0 max-lg:order-1">
            <div data-head-sub className="flex">
              <Eyebrow>Infrastructure &amp; availability</Eyebrow>
            </div>
            <h2 className="mt-5 text-[clamp(30px,4vw,48px)] font-bold leading-[1.06] tracking-[-0.027em]">
              Boring, on purpose.
            </h2>
            <p
              data-head-sub
              className="mt-[18px] max-w-[560px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-muted"
            >
              Separate read and write paths, a public status page, and a 99.99% SLA
              on Enterprise — availability is a security property here, not a marketing one.
            </p>
            <div className="mt-6 flex flex-col gap-3.5">
              {INFRA_CHECKS.map((check) => (
                <div key={check} data-check-row>
                  <PinkCheck>{check}</PinkCheck>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
